"use client";
import { motion } from "framer-motion";
import Link from "next/link";

const principles = [
  {
    title: "1. Acceptance of Terms",
    description:
      `By accessing Lindy Balinese Cats' website, submitting inquiries, joining our waiting list, or engaging in any adoption services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.`
  },
  {
    title: "2. Eligibility Requirements",
    description:
      `To engage with LINDY's services, you must be at least 18 years old and legally capable of entering into binding agreements. By using our services, you represent that you meet these requirements. We reserve the right to decline services or terminate agreements if we determine you do not meet our adoption standards or these eligibility requirements.`
  },
  {
    title: "3. Description of Services",
    points: [
      "LINDY operates as a premium Balinese cat breeding and adoption service, offering consultation, kitten placement, and ongoing support.",
      "Our services include temperament evaluation, health documentation, adoption counseling, and post-adoption guidance.",
      "We maintain a curated waiting list and conduct thorough compatibility assessments before making placement recommendations.",
      "All services are provided in accordance with applicable veterinary standards, animal welfare regulations, and industry best practices."
    ]
  },
  {
    title: "4. Adoption Process & Requirements",
    points: [
      "**Consultation Phase**: Initial discussions to assess compatibility between guardians and available kittens, including lifestyle evaluation and expectations alignment.",
      "**Reservation & Deposit**: Upon mutual agreement, a reservation deposit secures placement priority. Deposit amounts and refund policies are detailed in individual adoption agreements.",
      "**Health & Documentation**: All kittens depart with comprehensive health records, vaccinations, microchipping, and pedigree documentation as required by law.",
      "**Post-Adoption Support**: Ongoing guidance for the first year following placement, including care instructions and access to our support network.",
      "**Breeding & Transfer Rights**: Specific restrictions on breeding, transfer, or rehoming are outlined in individual adoption contracts and must be strictly followed."
    ]
  },
  {
    title: "5. Guardian Responsibilities",
    points: [
      "**Care Standards**: Provide appropriate nutrition, veterinary care, grooming, and living conditions that meet or exceed industry standards for feline welfare.",
      "**Health Monitoring**: Schedule regular veterinary checkups, maintain vaccination schedules, and promptly report any health concerns to both your veterinarian and LINDY.",
      "**Legal Compliance**: Adhere to all local, state, and federal regulations regarding pet ownership, licensing, and animal welfare requirements.",
      "**Communication**: Keep LINDY informed of address changes, contact information updates, and any significant life changes that may affect the kitten's welfare.",
      "**Contractual Obligations**: Fulfill all terms outlined in the individual adoption agreement, including spay/neuter requirements, breeding restrictions, and return policies."
    ]
  },
  {
    title: "6. Fees, Payments & Refunds",
    points: [
      "**Adoption Fees**: Published fees cover breeding, care, health documentation, and initial supplies. Additional costs for transportation, insurance, or special requirements are separate.",
      "**Payment Methods**: We accept approved payment methods through secure third-party processors. All transactions are subject to applicable taxes and processing fees.",
      "**Deposit Policy**: Reservation deposits are applied to the total adoption fee. Refund eligibility depends on timing and circumstances as specified in the adoption agreement.",
      "**Payment Schedule**: Full payment is typically required before kitten departure unless alternative arrangements are agreed upon in writing.",
      "**Late Fees**: Overdue payments may incur late fees as specified in individual agreements or service contracts."
    ]
  },
  {
    title: "7. Intellectual Property Rights",
    points: [
      "**LINDY Content**: All website content, photography, breeding records, logos, and marketing materials are owned exclusively by Lindy Balinese Cats and protected by copyright laws.",
      "**Usage Restrictions**: Reproduction, distribution, or commercial use of our materials requires prior written permission. Personal sharing for non-commercial purposes is permitted with attribution.",
      "**Trademarks**: \"Lindy Balinese Cats\" and related branding are registered or common law trademarks. Unauthorized use is prohibited.",
      "**User Content**: Testimonials, photos, or content shared with LINDY may be used for marketing purposes with your consent, which can be withdrawn at any time."
    ]
  },
  {
    title: "8. User Conduct & Platform Usage",
    points: [
      "**Prohibited Activities**: Do not attempt unauthorized access, interfere with website operations, or engage in fraudulent, abusive, or illegal activities.",
      "**Community Standards**: Maintain respectful communication with LINDY staff and other community members. Harassment, discrimination, or inappropriate behavior will not be tolerated.",
      "**Content Guidelines**: When sharing content on our platforms, ensure it complies with applicable laws and does not infringe on third-party rights.",
      "**Account Security**: You are responsible for maintaining the confidentiality of your account credentials and all activities under your account."
    ]
  },
  {
    title: "9. Health Guarantees & Warranties",
    points: [
      "**Health Warranty**: We provide health guarantees as specified in individual adoption agreements, typically covering congenital conditions for a defined period.",
      "**Veterinary Requirements**: Guardians must provide veterinary documentation for any warranty claims and follow our recommended health care protocols.",
      "**Warranty Limitations**: Guarantees do not cover conditions resulting from inadequate care, accidents, or normal aging processes.",
      "**Return Policy**: Specific return conditions, timelines, and procedures are outlined in adoption contracts. Returns may be subject to restocking or administrative fees."
    ]
  },
  {
    title: "10. Limitation of Liability",
    points: [
      "**Service Limitations**: While we strive for excellence, we cannot guarantee specific outcomes from the adoption process or guarantee against all potential health or behavioral issues.",
      "**Indirect Damages**: To the maximum extent permitted by law, LINDY shall not be liable for indirect, incidental, consequential, or punitive damages.",
      "**Force Majeure**: We are not liable for delays or failures caused by circumstances beyond our reasonable control, including natural disasters, pandemics, or regulatory changes.",
      "**Third-Party Services**: We are not responsible for the actions, errors, or omissions of third-party service providers such as veterinarians, transporters, or payment processors."
    ]
  },
  {
    title: "11. Indemnification",
    description:
      `You agree to indemnify and hold harmless Lindy Balinese Cats, its owners, employees, and agents from any claims, damages, losses, or expenses arising from your breach of these Terms, violation of applicable laws, or negligence in caring for adopted kittens.`
  },
  {
    title: "12. Dispute Resolution",
    points: [
      "**Mediation First**: Parties agree to attempt resolution through mediation before pursuing other legal remedies.",
      "**Governing Law**: These Terms are governed by the laws of the State of Oregon, United States, without regard to conflict of law principles.",
      "**Jurisdiction**: Any legal disputes shall be resolved in the courts of Oregon, and you consent to personal jurisdiction in those courts.",
      "**Class Action Waiver**: You agree to resolve disputes individually and waive any right to participate in class action lawsuits against LINDY.",
      "**Statute of Limitations**: Any claims must be brought within one year of the date the claim arises."
    ]
  },
  {
    title: "13. Privacy & Data Protection",
    description:
      `Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our services, you consent to the data practices described in our Privacy Policy.`
  },
  {
    title: "14. Termination of Services",
    points: [
      "**LINDY's Rights**: We reserve the right to terminate services, cancel reservations, or refuse future services for violations of these Terms or other reasonable business reasons.",
      "**User Termination**: You may discontinue services at any time, subject to any contractual obligations in adoption agreements.",
      "**Effect of Termination**: Upon termination, all rights and obligations under active adoption agreements remain in effect until fulfilled.",
      "**Data Retention**: We may retain certain information as required by law or for legitimate business purposes even after service termination."
    ]
  },
  {
    title: "15. Severability & Entire Agreement",
    points: [
      "**Severability**: If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.",
      "**Entire Agreement**: These Terms, together with our Privacy Policy and any individual adoption agreements, constitute the entire agreement between you and LINDY.",
      "**Amendments**: We may modify these Terms at any time. Continued use of our services after changes constitutes acceptance of the modified Terms.",
      "**Precedence**: In case of conflict, individual adoption agreements take precedence over these general Terms."
    ]
  },
  {
    title: "16. Contact Information",
    points: [
      "**General Inquiries**: For questions about these Terms or our services, contact us through our website or at the information provided below.",
      "**Legal Notices**: Formal legal notices should be sent to our registered address or through certified mail.",
      "**Business Hours**: Our team is available Monday through Friday, 9:00 AM to 6:00 PM Pacific Time, excluding holidays.",
      "**Contact Details**: Email: concierge@lindybalinesecats.com | Phone: +1 (606) 465-2993 | Address: Available upon request for formal correspondence."
    ]
  }
];

export default function TermsPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <div className="max-w-5xl mx-auto px-6 py-16 space-y-16">
        <motion.section
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-soft-gold mb-4">Terms of Service</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-warm-brown mb-6">
            LINDY Terms of Service & Legal Framework
          </h1>
          <p className="text-xl text-warm-brown/70 max-w-3xl mx-auto">
            Our comprehensive terms establish clear expectations, protect all parties, and ensure a transparent, legally-compliant adoption experience for every guardian.
          </p>
        </motion.section>

        <motion.section
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 space-y-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {principles.map((section, index) => (
            <div key={section.title} className="space-y-4">
              <h2 className="font-heading text-2xl text-warm-brown flex items-center gap-3">
                <span className="text-soft-gold text-lg">{String(index + 1).padStart(2, '0')}</span>
                {section.title}
              </h2>
              {section.description && (
                <p className="text-warm-brown/75 leading-relaxed">
                  {section.description}
                </p>
              )}
              {section.points && (
                <ul className="space-y-2 text-warm-brown/75 leading-relaxed">
                  {section.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <span className="mt-1 text-soft-gold">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </motion.section>

        <motion.section
          className="bg-gradient-to-r from-soft-gold/10 to-cream rounded-3xl p-10 shadow-xl text-center space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="font-heading text-3xl text-warm-brown mb-2">Ready to begin your adoption journey?</h2>
          <p className="text-warm-brown/70 max-w-2xl mx-auto">
            Our comprehensive legal framework ensures transparency and protection for all parties. Individual adoption agreements complement these terms with specific details about your kitten placement, health guarantees, and ongoing support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="px-6 py-3 bg-soft-gold text-white rounded-xl shadow-lg hover:shadow-xl transition-all font-medium"
            >
              Contact Legal Team
            </Link>
            <Link
              href="/privacy"
              className="px-6 py-3 border border-soft-gold text-soft-gold rounded-xl hover:bg-soft-gold hover:text-white transition-all font-medium"
            >
              View Privacy Policy
            </Link>
          </div>
        </motion.section>
      </div>
    </main>
  );
}
